<!-- src/lib/components/TabBar.svelte -->
<!--
  32px horizontal tab strip between the view-topbar and main content.

  • View tabs: small icon-like abbreviation + label text
  • Card tabs: human-id chip + truncated title
  • × button on hover to close; middle-click also closes
  • After ~10 visible tabs a ··· dropdown lists all tabs
  • Drag-to-reorder (HTML5 drag API)
  • Active tab has a purple bottom underline
-->
<script lang="ts">
  import {
    tabStore,
    openTab,
    closeTab,
    activateTab,
    reorderTab,
    type TabEntry,
  } from '../stores/tabs.svelte.ts';

  interface Props {
    workspaceId:  string;
    /** Called when the active tab changes. Parent uses this to sync activeTab/openCardId. */
    onActivate:   (tab: TabEntry) => void;
  }
  let { workspaceId, onActivate }: Props = $props();

  // ── Overflow management ────────────────────────────────────────────────
  const MAX_VISIBLE = 10;
  const visibleTabs = $derived(tabStore.tabs.slice(0, MAX_VISIBLE));
  const overflowTabs = $derived(tabStore.tabs.slice(MAX_VISIBLE));
  let overflowOpen = $state(false);

  // ── Drag-to-reorder ────────────────────────────────────────────────────
  let dragFromIdx = $state<number | null>(null);
  let dragOverIdx = $state<number | null>(null);

  function onDragStart(e: DragEvent, idx: number) {
    dragFromIdx = idx;
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', String(idx));
    }
  }
  function onDragOver(e: DragEvent, idx: number) {
    e.preventDefault();
    dragOverIdx = idx;
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
  }
  function onDrop(e: DragEvent, toIdx: number) {
    e.preventDefault();
    if (dragFromIdx !== null && dragFromIdx !== toIdx) {
      reorderTab(workspaceId, dragFromIdx, toIdx);
      // After reorder the active tab is the one we dragged.
      const active = tabStore.tabs[tabStore.activeIdx];
      if (active) onActivate(active);
    }
    dragFromIdx = null;
    dragOverIdx = null;
  }
  function onDragEnd() {
    dragFromIdx = null;
    dragOverIdx = null;
  }

  // ── Tab icon abbreviations for view tabs ──────────────────────────────
  const viewIcons: Record<string, string> = {
    overview:   '⊞',
    board:      '⬜',
    table:      '≡',
    gallery:    '⊞',
    canvas:     '◯',
    agents:     '▶',
    activity:   '≈',
    tasks:      '✓',
    goals:      '◎',
    milestones: '⬟',
    portfolio:  '⊛',
    workload:   '⊜',
    pulse:      '∿',
    rules:      '⚙',
    personas:   '◉',
    chat:       '☰',
    brand:      '⊿',
    drive:      '△',
    studio:     '◇',
    dev:        '⌨',
  };

  function tabIcon(tab: TabEntry): string {
    if (tab.type === 'view') return viewIcons[tab.key] ?? '·';
    // Card tab — show nothing (human_id chip is the visual anchor)
    return '';
  }

  // ── Click handler ─────────────────────────────────────────────────────
  function handleTabClick(idx: number) {
    activateTab(idx);
    const tab = tabStore.tabs[idx];
    if (tab) onActivate(tab);
  }

  // ── Close handler ─────────────────────────────────────────────────────
  function handleClose(e: MouseEvent | KeyboardEvent, idx: number) {
    e.stopPropagation();
    const wasActive = idx === tabStore.activeIdx;
    closeTab(workspaceId, idx);
    // Notify parent of the new active tab (if any remain).
    const next = tabStore.tabs[tabStore.activeIdx];
    if (next && wasActive) onActivate(next);
  }

  // ── Middle-click close ─────────────────────────────────────────────────
  function handleMouseDown(e: MouseEvent, idx: number) {
    if (e.button === 1) {
      e.preventDefault();
      handleClose(e, idx);
    }
  }

  // ── Overflow dropdown ──────────────────────────────────────────────────
  function selectOverflow(idx: number) {
    overflowOpen = false;
    handleTabClick(idx + MAX_VISIBLE);
  }

  // ── Card label helper ─────────────────────────────────────────────────
  function isCardHumanId(label: string): boolean {
    // Human IDs look like "LOC-17" — prefix + dash + number
    return /^[A-Z]{2,6}-\d+$/.test(label.split(' ')[0] ?? '');
  }
</script>

{#if tabStore.tabs.length > 0}
<div class="tab-bar" role="tablist" aria-label="Open tabs">
  {#each visibleTabs as tab, idx (tab.type + ':' + tab.key + ':' + idx)}
    <!-- Use div+role=tab so we can nest the close button without invalid HTML -->
    <div
      role="tab"
      tabindex={0}
      aria-selected={idx === tabStore.activeIdx}
      class="tab"
      class:active={idx === tabStore.activeIdx}
      class:drag-over={dragOverIdx === idx}
      draggable={true}
      onclick={() => handleTabClick(idx)}
      onkeydown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleTabClick(idx); }}
      onmousedown={(e) => handleMouseDown(e, idx)}
      ondragstart={(e) => onDragStart(e, idx)}
      ondragover={(e) => onDragOver(e, idx)}
      ondrop={(e) => onDrop(e, idx)}
      ondragend={onDragEnd}
      title={tab.label}
    >
      {#if tab.type === 'view'}
        <span class="tab-icon" aria-hidden="true">{tabIcon(tab)}</span>
        <span class="tab-label">{tab.label}</span>
      {:else}
        <!-- Card tab: chip (human id or short uuid) + truncated title -->
        {@const parts = tab.label.split(' ')}
        {@const chip = isCardHumanId(parts[0] ?? '') ? parts[0] : tab.key.slice(0, 7)}
        {@const title = isCardHumanId(parts[0] ?? '') ? parts.slice(1).join(' ') : tab.label}
        <span class="tab-chip">{chip}</span>
        <span class="tab-label">{title}</span>
      {/if}
      <button
        class="tab-close"
        aria-label="Close tab"
        onclick={(e) => handleClose(e, idx)}
        onkeydown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleClose(e, idx); }}
        tabindex={-1}
      >×</button>
    </div>
  {/each}

  {#if overflowTabs.length > 0}
    <div class="tab-overflow">
      <button
        class="tab-overflow-btn"
        aria-expanded={overflowOpen}
        aria-haspopup="true"
        onclick={() => { overflowOpen = !overflowOpen; }}
        title="More tabs"
      >···</button>
      {#if overflowOpen}
        <div
          class="tab-overflow-menu"
          role="menu"
        >
          {#each overflowTabs as tab, i}
            <button
              role="menuitem"
              class="tab-overflow-item"
              class:active={i + MAX_VISIBLE === tabStore.activeIdx}
              onclick={() => selectOverflow(i)}
            >
              {tab.type === 'card' ? tab.label.split(' ')[0] + ' ' : ''}{tab.label}
            </button>
          {/each}
        </div>
        <!-- Click-away to close the overflow menu -->
        <div
          class="tab-overflow-backdrop"
          role="presentation"
          onclick={() => { overflowOpen = false; }}
        ></div>
      {/if}
    </div>
  {/if}
</div>
{/if}

<style>
  .tab-bar {
    display: flex;
    align-items: stretch;
    height: 32px;
    background: var(--bg-elevated);
    border-bottom: 1px solid var(--border-subtle);
    overflow: hidden;
    flex-shrink: 0;
    gap: 0;
  }

  .tab {
    display: inline-flex;
    align-items: center;
    gap: 4px;
    height: 100%;
    padding: 0 8px 0 10px;
    font-size: 11.5px;
    font-family: var(--font-sans);
    color: var(--text-secondary);
    background: transparent;
    border: none;
    border-right: 1px solid var(--border-faint);
    border-bottom: 2px solid transparent;
    cursor: pointer;
    white-space: nowrap;
    max-width: 160px;
    position: relative;
    transition:
      background var(--transition-fast),
      color var(--transition-fast);
  }

  .tab:hover {
    background: var(--bg-contrast);
    color: var(--text-primary);
  }

  .tab.active {
    color: var(--text-primary);
    background: var(--bg-surface);
    border-bottom-color: var(--ab-purple-core);
  }

  .tab.drag-over {
    background: oklch(0.42 0.22 310 / 0.15);
    border-bottom-color: var(--ab-purple-bright);
  }

  .tab-icon {
    font-size: 10px;
    opacity: 0.7;
    flex-shrink: 0;
  }

  .tab-chip {
    font-family: var(--font-mono);
    font-size: 10px;
    padding: 1px 4px;
    background: oklch(0.42 0.22 310 / 0.25);
    color: var(--ab-purple-bright);
    border-radius: var(--radius-sm);
    flex-shrink: 0;
    line-height: 1.4;
  }

  .tab-label {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    max-width: 100px;
  }

  .tab-close {
    display: none;
    align-items: center;
    justify-content: center;
    width: 14px;
    height: 14px;
    font-size: 12px;
    line-height: 1;
    padding: 0;
    margin-left: 2px;
    border: none;
    border-radius: 3px;
    background: transparent;
    color: var(--text-muted);
    cursor: pointer;
    flex-shrink: 0;
    transition: background var(--transition-fast), color var(--transition-fast);
  }

  .tab:hover .tab-close,
  .tab.active .tab-close {
    display: inline-flex;
  }

  .tab-close:hover {
    background: oklch(0.62 0.26 25 / 0.25);
    color: var(--ab-red-bright);
  }

  /* ── Overflow ──────────────────────────────────────────────────────── */

  .tab-overflow {
    position: relative;
    display: flex;
    align-items: center;
  }

  .tab-overflow-btn {
    height: 100%;
    padding: 0 10px;
    font-size: 13px;
    letter-spacing: 1px;
    color: var(--text-muted);
    background: transparent;
    border: none;
    border-right: 1px solid var(--border-faint);
    cursor: pointer;
    transition: color var(--transition-fast);
  }

  .tab-overflow-btn:hover {
    color: var(--text-primary);
    background: var(--bg-contrast);
  }

  .tab-overflow-menu {
    position: absolute;
    top: calc(100% + 2px);
    right: 0;
    min-width: 220px;
    background: var(--bg-overlay);
    border: 1px solid var(--border-subtle);
    border-radius: var(--radius-md);
    box-shadow: 0 8px 24px oklch(0 0 0 / 0.5);
    z-index: 100;
    padding: 4px 0;
    max-height: 320px;
    overflow-y: auto;
  }

  .tab-overflow-item {
    display: block;
    width: 100%;
    text-align: left;
    padding: 6px 14px;
    font-size: 12px;
    color: var(--text-secondary);
    background: transparent;
    border: none;
    cursor: pointer;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    transition: background var(--transition-fast), color var(--transition-fast);
  }

  .tab-overflow-item:hover,
  .tab-overflow-item.active {
    background: var(--bg-contrast);
    color: var(--text-primary);
  }

  .tab-overflow-backdrop {
    position: fixed;
    inset: 0;
    z-index: 99;
  }
</style>
